import FileSaver from 'file-saver';
import minBy from 'lodash/minBy';

import * as IDBService from './IndexedDBHelper';

/*                        USER LIBRARY CONTENT PIDS                            */
const transformToAndFromIDBFormat = (libraryContentPids, reverse) => {
  if (reverse) {
    return (libraryContentPids[0] && libraryContentPids[0].libraryContentPids) || [];
  }
  return {
    keyName: 'libraryContentPids',
    libraryContentPids
  };
};
export function saveAllLibraryContentPids(libraryContentPids) {
  const pids = transformToAndFromIDBFormat(libraryContentPids);
  return IDBService.writeData(IDBService.LIBRARY_CONTENT_PIDS, pids);
}

window.IDBService = IDBService;

export async function getAllLibraryContentPids() {
  const libraryContentPids = await IDBService.readAllData(IDBService.LIBRARY_CONTENT_PIDS);
  return transformToAndFromIDBFormat(libraryContentPids, true);
}

/*                        CUSTOM PROPERTIES                            */
function transformCustomproperty(customProperty) {
  const id = customProperty._id;
  return {
    ...customProperty,
    pid: id,
    propertyId: id
  };
}

export function saveAllCustomProperties(customProperties) {
  return Promise.all(
    customProperties.map(customProperty => {
      const transformedCustomProperty = transformCustomproperty(customProperty);
      return IDBService.writeData(IDBService.CUSTOM_PROPERTIES_STORE, transformedCustomProperty);
    })
  );
}

export function saveCustomProperty(customProperty) {
  const transformedCustomProperty = transformCustomproperty(customProperty);
  return IDBService.writeData(IDBService.CUSTOM_PROPERTIES_STORE, transformedCustomProperty);
}

export function getCustomProperty(propertyId) {
  return IDBService.readData(IDBService.CUSTOM_PROPERTIES_STORE, propertyId);
}

export function getCustomProperties(customPropertyIds) {
  return Promise.all(
    customPropertyIds.map(customPropertyId => getCustomProperty(customPropertyId))
  );
}

export function getAllCustomProperties() {
  return IDBService.readAllData(IDBService.CUSTOM_PROPERTIES_STORE);
}

/*                        ENROLMENTS                            */

function transformEnrolment(enrolment) {
  const uniqueKey = `${enrolment.userId}_${enrolment.contentId}`;
  return {
    ...enrolment,
    userId_contentId: uniqueKey
  };
}

export function saveAllEnrolments(enrolments) {
  const enrolmentsToWrite = [];
  enrolments.forEach(enrolment => {
    if (enrolment && enrolment.contentId && enrolment.userId) {
      const transformedEnrolment = transformEnrolment(enrolment);
      enrolmentsToWrite.push(transformedEnrolment);
    }
  });
  return IDBService.writeBatchData(IDBService.ENROLMENT_STORE, enrolmentsToWrite);
}

export function saveEnrolment(enrolment) {
  const enrolmentsArray = Array.isArray(enrolment) ? enrolment : [enrolment];
  const transformedEnrolments = enrolmentsArray.map(transformEnrolment);
  return Promise.all(
    transformedEnrolments.map(e => IDBService.writeData(IDBService.ENROLMENT_STORE, e))
  );
}

export function getEnrolment(userId, contentId) {
  const searchKey = `${userId}_${contentId}`;
  return IDBService.readData(IDBService.ENROLMENT_STORE, searchKey);
}

window.getEnrolment = getEnrolment;

export function getMultipleEnrolmentsById(enrolmentIds) {
  return Promise.all(enrolmentIds.map(key => IDBService.readData(IDBService.ENROLMENT_STORE, key)));
}

export function removeEnrolment(enrolmentId) {
  return IDBService.deleteItemFromData(IDBService.ENROLMENT_STORE, enrolmentId);
}

export function getAllEnrolments() {
  return IDBService.readAllData(IDBService.ENROLMENT_STORE);
}

/*                        OFFERS                            */

export function saveOffer(offer) {
  return IDBService.writeData(IDBService.OFFER_STORE, offer);
}

export function saveOffers(offers) {
  return Promise.all(offers.map(o => IDBService.writeData(IDBService.OFFER_STORE, o)));
}

export function getOffer(offerId) {
  return IDBService.readData(IDBService.OFFER_STORE, offerId);
}

export function getOffers(offerIds) {
  return Promise.all(offerIds.map(oId => IDBService.readData(IDBService.OFFER_STORE, oId)));
}

export function getAllOffers() {
  return IDBService.readAllData(IDBService.OFFER_STORE);
}

/*                        STATES                            */

function transformState(state) {
  const offerId = state.rootContentId_contentId.split('_')[0];
  const uniqueKey = `${state.userId}_${state.contentId}`;
  const indexKey = `${state.userId}_${offerId}`;
  return {
    ...state,
    userId_contentId: uniqueKey,
    userId_offerId: indexKey
  };
}

export function saveStates(states) {
  const statesToWrite = [];
  states.forEach(state => {
    if (state && state.contentId && state.userId && state.rootContentId_contentId) {
      const transformedState = transformState(state);
      statesToWrite.push(transformedState);
    }
  });
  return IDBService.writeBatchData(IDBService.STATE_STORE, statesToWrite);
}

export function getStatesForAsset(userId, assetId) {
  const searchKey = `${userId}_${assetId}`;
  return IDBService.readDataFromIndex(
    IDBService.STATE_STORE,
    IDBService.STATE_STORE_INDEX,
    searchKey
  );
}

export function getStateByUserIdContentId(userId, contentId) {
  const searchKey = `${userId}_${contentId}`;
  return IDBService.readData(IDBService.STATE_STORE, searchKey);
}

export function getMultipleStatesByUserIdContentIds(userId, contentIds) {
  return Promise.all(contentIds.map(cId => getStateByUserIdContentId(userId, cId)));
}

export function getStateById(stateId) {
  return IDBService.readData(IDBService.STATE_STORE, stateId);
}

export function getMultipleStatesById(stateIds) {
  return Promise.all(stateIds.map(id => getStateById(id)));
}

export function getAllStates() {
  return IDBService.readAllData(IDBService.STATE_STORE);
}

/*                        DATA SYNC                            */

export const DataSyncStatus = {
  QUEUED: 'QUEUED', // object added to local db, but not yet written to kinesis
  PROCESSED: 'PROCESSED' // successfully written object to kinesis
};

export function saveDataSyncObject(dataSyncObject) {
  return IDBService.writeData(IDBService.DATA_SYNC_STORE, dataSyncObject);
}

export function saveAllDataSyncObjects(dataSyncObjects) {
  return Promise.all(dataSyncObjects.map(dataSyncObject => saveDataSyncObject(dataSyncObject)));
}

export function getAllDataSyncObjects() {
  return IDBService.readAllData(IDBService.DATA_SYNC_STORE);
}

window.getAllDataSyncObjects = getAllDataSyncObjects;

export function setStatusForDataSyncObject(dataSyncObject, status) {
  dataSyncObject.status = status;
  return IDBService.writeData(IDBService.DATA_SYNC_STORE, dataSyncObject);
}

export async function getLatestUnprocessedDataSyncObject() {
  const dataSyncObjects = await getAllDataSyncObjects();
  const queuedDataSyncObjects = dataSyncObjects.filter(obj => obj.status === DataSyncStatus.QUEUED);
  return minBy(queuedDataSyncObjects, 'timeStamp');
}

export function getDataSyncObjectById(dataSyncId) {
  return IDBService.readData(IDBService.DATA_SYNC_STORE, dataSyncId);
}

export function getDataSyncObjectByMultipleIds(dataSyncIds) {
  return Promise.all(dataSyncIds.map(id => IDBService.readData(IDBService.DATA_SYNC_STORE, id)));
}

export function removeDataSyncObjectWithId(dataSyncId) {
  return IDBService.deleteItemFromData(IDBService.DATA_SYNC_STORE, dataSyncId);
}

export function removeMultipleDataSyncObjectsWithId(dataSyncIds) {
  if (dataSyncIds && dataSyncIds.length > 0) {
    return Promise.all(dataSyncIds.map(id => removeDataSyncObjectWithId(id)));
  }
  return Promise.resolve();
}

window.removeMultipleDataSyncObjectsWithId = removeMultipleDataSyncObjectsWithId;

export function removeAllDataSyncObjects() {
  return IDBService.clearAllData(IDBService.DATA_SYNC_STORE);
}

/*                        STATE INTERACTION                            */

function saveStateIntxn(stateIntxn) {
  return IDBService.writeData(IDBService.STATE_INTERACTION_STORE, stateIntxn);
}

export function saveStateInteractions(stateInteractions) {
  return Promise.all(stateInteractions.map(stateInteraction => saveStateIntxn(stateInteraction)));
}

export function getStateInteractionsById(contentId, interactionType, interactingOnId, actorId) {
  const searchKey = `${contentId}_${interactionType}_${interactingOnId}_${actorId}}`;
  return IDBService.readData(IDBService.STATE_INTERACTION_STORE, searchKey);
}

export function getMultipleStateInteractionsById(searchKeys) {
  return Promise.all(
    searchKeys.map(key => IDBService.readData(IDBService.STATE_INTERACTION_STORE, key))
  );
}

export function getAllStateInteractions() {
  return IDBService.readAllData(IDBService.STATE_INTERACTION_STORE);
}

/*                        CONTENT SETTINGS                            */

export function saveAllContentSettings(allContentSettings) {
  const contentSettingsToWrite = [];
  allContentSettings.forEach(contentSetting => {
    if (contentSetting && contentSetting.contentId) {
      contentSettingsToWrite.push(contentSetting);
    }
  });
  return IDBService.writeBatchData(IDBService.CONTENT_SETTINGS_STORE, contentSettingsToWrite);
}

export function saveContentSetting(contentSetting) {
  return IDBService.writeData(IDBService.CONTENT_SETTINGS_STORE, contentSetting);
}

export function getContentSetting(contentId) {
  try {
    return IDBService.readData(IDBService.CONTENT_SETTINGS_STORE, contentId);
  } catch (e) {
    console.log('error in reading Content settings');
    console.error(e);
  }
}

export function getAllContentSetting() {
  return IDBService.readAllData(IDBService.CONTENT_SETTINGS_STORE);
}

export function getContentSettingsByIds(contentIds) {
  try {
    return Promise.all(contentIds.map(contentId => getContentSetting(contentId)));
  } catch (e) {
    console.log('error in reading Content settings');
    console.error(e);
  }
}

/*                        USER                            */

export function getUserById(userId) {
  return IDBService.readData(IDBService.USER_STORE, userId);
}

export function saveUser(user) {
  return IDBService.writeData(IDBService.USER_STORE, user);
}

/*                        COMPANY                            */

export function getCompanyById(companyId) {
  return IDBService.readData(IDBService.COMPANY_STORE, companyId);
}

export function saveCompany(company) {
  return IDBService.writeData(IDBService.COMPANY_STORE, company);
}

/* --------------------- CONTENT_DETAILS -------------------- */

export function saveContentDetails(contentId, contentDetails) {
  return IDBService.writeData(IDBService.CONTENT_DETAILS_STORE, { contentId, ...contentDetails });
}

export function getContentDetailsByContentId(contentId) {
  return IDBService.readData(IDBService.CONTENT_DETAILS_STORE, contentId);
}

export async function __downloadUserDataFromDBAsJSON__() {
  const stores = IDBService.ALL_STORES;
  const completeData = {};
  const promises = [];
  stores.forEach(store => {
    promises.push(IDBService.readAllData(store));
  });
  const data = await Promise.all(promises);
  data.forEach((d, i) => {
    completeData[stores[i]] = d;
  });
  const json = JSON.stringify(completeData);
  const blob = new Blob([json], { type: 'application/json' });
  FileSaver.saveAs(blob, 'user_all_data.json');
}

window.__downloadUserDataFromDBAsJSON__ = __downloadUserDataFromDBAsJSON__;
